import { AlertTriangle, ShieldCheck, Lightbulb, Sparkles } from 'lucide-react';

export const telephoneArticle = {
  slug: 'telephone-oreillettes-moto',
  weekVolume: 26,
  weekYear: 2026,
  monthLabel: 'Juillet 2026',
  category: 'Digital',
  shortTitle: 'Téléphone & oreillettes',
  title: 'Téléphone & oreillettes',
  kicker: 'Article R412-6-1 — distractions au guidon',
  subtitle: "Depuis 2015, les oreillettes et casques audio sont <strong>strictement interdits</strong> au guidon. Mais le Bluetooth intégré au casque, lui, reste autorisé. On démêle tout.",
  heroImage: 'https://images.unsplash.com/photo-1608501622839-8a8955239276?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: 'Smartphone et casque moto Bluetooth',
  topRightKicker: { line1: 'Conseil ECSR', line2: 'Digital' },
  readTimeMin: 3,
  seoDescription: "Téléphone, oreillettes et Bluetooth en moto : ce que dit l'article R412-6-1 en 2026. Sanctions et solutions autorisées.",
  seoKeywords: 'oreillette moto, téléphone moto, R412-6-1, intercom moto, bluetooth casque',
  source: { label: 'Sécurité routière', url: 'https://www.securite-routiere.gouv.fr', refs: 'Article R412-6-1 du Code de la route, modifié le 1er juillet 2015.' },
  chapo: {
    kicker: 'En bref',
    body: "Depuis le <strong>1ʳ juillet 2015</strong>, le port à l’oreille de tout dispositif <strong>émetteur de son</strong> (oreillette, casque audio, kit piéton, écouteurs) est <strong>interdit</strong> en conduite. Les <strong>haut-parleurs intégrés au casque moto</strong> (intercom Bluetooth) restent <strong>autorisés</strong>.",
    sideStat: { kicker: 'Sanction', big: '135 €', body: "Amende <strong>4ᵉ classe</strong> + retrait de <strong>3 points</strong>. Même une seule oreillette côté conducteur = infraction.", accent: '#cf2031' },
  },
  pullQuote: { text: 'Le téléphone tenu en main à moto = 135 € et 3 points. Même à l’arrêt au feu rouge. La sanction est immédiate et automatique.', source: "Article R412-6-1" },
  sections: [
    {
      part: 'Partie 1 — Ce qui est interdit',
      title: 'Les distractions sanctionnées',
      checkLists: [
        {
          kicker: 'Interdit — R412-6-1', accent: '#cf2031',
          items: [
            { ok: false, text: '<strong>Téléphone tenu en main</strong> (même à l’arrêt au feu)' },
            { ok: false, text: "<strong>Oreillette filaire ou sans fil</strong> dans l'oreille" },
            { ok: false, text: "<strong>Casque audio</strong> (écouteurs sur les oreilles)" },
            { ok: false, text: 'Écouteurs intra-auriculaires (même une seule oreille)' },
            { ok: false, text: 'Tablette posée sur le réservoir et manipulée en roulant' },
          ],
        },
        {
          kicker: 'Autorisé', accent: '#16a34a',
          items: [
            { ok: true, text: '<strong>Intercom Bluetooth intégré au casque</strong> (haut-parleurs sous la coque)' },
            { ok: true, text: '<strong>GPS dans le champ de vision</strong>, fixé sur le guidon' },
            { ok: true, text: 'Commande vocale sans toucher l’écran' },
            { ok: true, text: 'Appel d’urgence eCall (112 systématique après impact)' },
          ],
        },
      ],
    },
    {
      part: 'Partie 2 — Le piège des oreillettes',
      title: 'Pourquoi est-ce dangereux ?',
      paragraphs: [
        "Une <strong>oreillette dans l’oreille</strong> réduit ta perception du <strong>klaxon</strong>, des <strong>sirènes de secours</strong>, et des <strong>bruits d’accident imminent</strong>. À moto, l’ouïe est ton <strong>second œil</strong>.",
      ],
      callouts: [
        { icon: AlertTriangle, accent: '#cf2031', body: "<strong>Même une oreillette qui ne joue rien</strong> est considérée comme infraction. C’est la <strong>capacité à émettre du son</strong> dans l’oreille qui est interdite, pas son utilisation effective." },
        { icon: ShieldCheck, accent: '#16a34a', body: "<strong>Solution légale :</strong> intercom Bluetooth intégré au casque (Sena, Cardo…). Les haut-parleurs sont sous la coque, <strong>jamais dans l’oreille</strong>. 100 % autorisé." },
      ],
    },
    {
      part: 'Partie 3 — Le téléphone',
      title: 'Tenir son smartphone = sanction lourde',
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Sanction de base', title: 'Téléphone en main', body: "<strong>135 € + 3 points</strong>. Même à l’arrêt au feu rouge ou en bouchon. Même pour consulter une notif." },
        { icon: AlertTriangle, accent: '#7f1d1d', kicker: 'Aggravation 2020', title: 'Téléphone + autre infraction', body: "<strong>Rétention immédiate du permis</strong> pendant 72h, suspension jusqu’à 6 mois. Loi LOM 2019." },
        { icon: ShieldCheck, accent: '#16a34a', kicker: 'Solution légale', title: 'Support GPS + Bluetooth', body: "Smartphone fixé sur le guidon, contrôlé par <strong>commandes vocales</strong> via intercom. Aucun écran tactile." },
      ],
      cardsCols: 3,
    },
  ],
  quiz: [
    { id: 'q1', question: "Depuis quand les oreillettes sont-elles interdites au volant/guidon ?", options: ['1er janvier 2010', '1er juillet 2015', '1er janvier 2020', "Pas encore interdites"], correctIndex: 1, explain: "Article R412-6-1 modifié le 1er juillet 2015 : interdiction de tout dispositif émetteur de son porté à l’oreille." },
    { id: 'q2', question: "Le Bluetooth intégré au casque moto est-il autorisé ?", options: ['Non, comme les oreillettes', 'Oui, totalement', 'Uniquement à l’arrêt', "Seulement avec autorisation"], correctIndex: 1, explain: "Oui : les haut-parleurs sous la coque ne sont pas dans l’oreille = autorisés. Les intercoms Sena, Cardo, etc. sont légaux." },
    { id: 'q3', question: 'Quelle sanction pour utilisation du téléphone tenu en main à moto ?', options: ['90 € + 1 point', '135 € + 3 points', '68 € sans points', "Avertissement"], correctIndex: 1, explain: "135 € (4ᵉ classe) + 3 points. Même à l’arrêt au feu rouge ou dans un bouchon." },
    { id: 'q4', question: "En cas de téléphone + autre infraction (depuis 2020) ?", options: ["Sanctions normales additionnées", "Rétention immédiate du permis 72h", "Suspension immédiate 1 an", 'Annulation du permis'], correctIndex: 1, explain: "Loi LOM 2019 : rétention 72h + suspension jusqu'à 6 mois si téléphone + autre infraction (vitesse, stop…)." },
    { id: 'q5', question: "Avec une seule oreillette dans l'oreille gauche (passager côté), que risque le motard ?", options: ['Rien si c’est l’autre oreille', 'Rien si elle ne joue pas de musique', "135 € + 3 points (même une seule)", "Avertissement uniquement"], correctIndex: 2, explain: "Article R412-6-1 : <strong>une seule oreillette suffit</strong> pour caractériser l’infraction, peu importe l’oreille ou si elle émet du son." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: "L’équipement complet du motard",
    body: 'Casque, gants, blouson, dorsale… retrouve l’ensemble des EPI obligatoires et leur communication Bluetooth.',
    primary: { label: "Voir l'équipement complet", to: '/magazine/equipement-complet-motard-2026' },
    secondary: { label: 'Sanctions & barèmes', to: '/sanctions-baremes' },
  },
};
